import { computed, defineComponent, nextTick, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { withLoading } from '@hems/component';
import { UserReportService } from '@hems/service';
import { divideArray, getEnergyBalance, getParam, getDateInitialValue, getNowByClient, } from '@hems/user/src/containers/report/chartFunctions';
import { getGridOptions } from '@hems/user/src/containers/report/grid/chartOptions';
import { useLocale } from '@hems/util';
import { getTime } from '@hems/util/src/helper/dateHelper';
import { GraphChart, EnergyBalance, CalendarHourly, CalendarDaily, CalendarMonth } from '@/components';
export default defineComponent({
    name: 'GridContainer',
    components: {
        GraphChart,
        EnergyBalance,
        CalendarHourly,
        CalendarDaily,
        CalendarMonth,
    },
    props: {
        tabname: {
            type: String,
            required: true,
            default: 'hourly',
        },
    },
    setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const state = reactive({
            tabname: props.tabname,
            showChart: false,
            data: {
                feedInData: [],
                demandData: [],
            },
            energyBalance: [],
            date: null,
        });
        const defaultBaseDeviceInfo = {
            device_id: '',
            site_id: 0,
            timezone_id: '',
            product_model_nm: '',
        };
        const { intlFormatLocale } = useLocale();
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const timezoneId = computed(() => store.state.appCtx.timeZone ?? '');
        const service = new UserReportService(window.axiosInstance.axios);
        const options = computed(() => getGridOptions(state.tabname, intlFormatLocale.value, state.data, t));
        async function getReportsByInterval(tabname) {
            if (state.date == null)
                return;
            const date = state.date;
            await withLoading(async () => {
                await service
                    .getReportsByInterval(tabname, getParam(date, 'demand,feedin', state.tabname, baseDeviceInfo.value ?? defaultBaseDeviceInfo))
                    .then(({ result }) => {
                    state.energyBalance = divideArray([
                        { value: result.demand_kwh.origin || 0, unit: 'kWh', type: 'device.imported' },
                        { value: result.feedin_kwh.origin || 0, unit: 'kWh', type: 'device.exported' },
                    ], 2);
                    state.data.feedInData = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.feedin_data[index] || 0,
                    }));
                    state.data.demandData = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.demand_data[index] || 0,
                    }));
                })
                    .catch((e) => {
                    console.error(e);
                    state.energyBalance = [[], [], [], [], []];
                    state.data.feedInData = [];
                    state.data.demandData = [];
                });
            })();
            nextTick(() => (state.showChart = true));
        }
        function rerenderDataGrid() {
            state.showChart = false;
            nextTick(() => (state.showChart = true));
        }
        function getServerTime() {
            service
                .getServerTime(baseDeviceInfo.value?.timezone_id ?? '')
                .then(async (res) => {
                state.date = getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = getDateInitialValue(getNowByClient(timezoneId.value));
            });
        }
        watch(() => props.tabname, (tabname) => {
            state.tabname = tabname;
        });
        watch(() => state.tabname, () => {
            state.showChart = false;
            getReportsByInterval(state.tabname);
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            getReportsByInterval(state.tabname);
        }, {
            deep: true,
        });
        watch(() => intlFormatLocale.value, () => {
            rerenderDataGrid();
        });
        getServerTime();
        return {
            state,
            options,
            energyBalance: computed(() => getEnergyBalance(state.energyBalance, intlFormatLocale.value, t)),
        };
    },
});
